import { Button } from '@civilgrid/ui/components';
import { useSnapshot } from 'valtio';
import { orderPrelimTitleReport as apiOrderPrelimTitleReport } from '~/api/title2';
import { Action } from '~/authorizations';
import Can from '~/authorizations/Can';
import { getReasonForError } from '~/common/getReasonForError';
import { captureTypedPosthogEvent, POSTHOG_EVENTS } from '~/common/posthogEvents';
import type { Project } from '~/common/types';
import { Notify } from '~/components2/Notify';
import { getParcelDetails, getParcelIdsFromSelectedIds } from '~/pages/projectDetails/landRights/helper';
import store from '~/pages/projectDetails/landRights/store';
import { DocType, OrderStep } from '~/pages/projectDetails/landRights/types';
import rootStore from '~/store';
import { parcelDataCost } from './constant';

interface ButtonsProps {
  closeDialog: () => void;
  projectId: string;
  docType: DocType;
  project?: Project;
  setProject: (project: Project) => void;
  makeParcelRequest: ({ docType }: { docType: DocType }) => void;
  makeReprintRequest?: () => void;
}

export const Buttons = ({
  closeDialog,
  projectId,
  docType,
  project,
  setProject,
  makeParcelRequest,
  makeReprintRequest,
}: ButtonsProps) => {
  const { parcelDetails, selectedIds, documentOrderStep } = useSnapshot(store) as typeof store;

  switch (documentOrderStep) {
    case OrderStep.Confirm:
      return (
        <>
          <Button type="reset" variant="secondary" onClick={closeDialog} data-testid="land-rights-cancel-button">
            Cancel
          </Button>
          <Can action={parcelDataCost[docType].getAmount() > 0 ? Action.OrderPaidReports : Action.OrderFreeReports}>
            <Button
              type="submit"
              data-testid="land-rights-confirm-button"
              onClick={async () => {
                if (docType === DocType.PrelimTitleReport) {
                  try {
                    await apiOrderPrelimTitleReport({
                      parcelIds: await getParcelIdsFromSelectedIds(selectedIds),
                      projectId,
                    });
                  } catch (err) {
                    captureTypedPosthogEvent(POSTHOG_EVENTS.landRightsDataDownloadFailed, {
                      project_id: projectId,
                      organization_id: rootStore.currentOrganizationId || undefined,
                      type: DocType.PrelimTitleReport,
                      reason: getReasonForError(err),
                    });
                    Notify.error({
                      title: '⚠️',
                      message: 'There was an error ordering your Prelim Title Report. Please contact Support.',
                    });
                    closeDialog();
                    return;
                  }

                  Notify.info({
                    title: 'Prelim Title Report',
                    message: 'A CivilGrid representative will contact you shortly to process your order.',
                  });

                  captureTypedPosthogEvent(POSTHOG_EVENTS.landRightsDataDownloaded, {
                    project_id: projectId,
                    organization_id: rootStore.currentOrganizationId || undefined,
                    type: DocType.PrelimTitleReport,
                  });

                  closeDialog();
                } else {
                  const selectedParcelAPNs = await getParcelIdsFromSelectedIds(selectedIds);

                  const unLoaded = parcelDetails.filter(
                    (details) => selectedParcelAPNs.includes(details.uniqueId) && Object.keys(details).length < 2,
                  );
                  const parcelDetailsCalls = unLoaded.map(({ parcelId }) =>
                    getParcelDetails({
                      parcelId,
                      projectId,
                      setProjectData: setProject,
                      project,
                      parcelDetails,
                    }),
                  );
                  await Promise.allSettled(parcelDetailsCalls);

                  makeParcelRequest?.({ docType });

                  if (docType === DocType.ChainOfTitleReport) {
                    // NOTE: temporarily separating because if the job fails in the BE, it doesn't return any error to the FE. Until we can check if the job is done, we'll just close the modal.
                    closeDialog();
                  } else {
                    store.updateDocumentOrderStep(OrderStep.Processing);
                  }
                }
              }}
            >
              Confirm
            </Button>
          </Can>
        </>
      );
    case OrderStep.Processing:
      return (
        <>
          <Button type="reset" variant="secondary" onClick={closeDialog} data-testid="land-rights-close-button">
            Close
          </Button>
          <Button loading={true} disabled>
            Processing
          </Button>
        </>
      );
    case OrderStep.View:
      return (
        <>
          <Button type="reset" variant="secondary" onClick={closeDialog} data-testid="land-rights-close-button">
            Close
          </Button>
          <Button
            type="submit"
            variant="secondary"
            data-testid="land-rights-download-pdf-button"
            onClick={async () => {
              makeReprintRequest?.();
            }}
          >
            Download Table Summary
          </Button>
        </>
      );
    default:
      return (
        <Button
          type="submit"
          data-testid="land-rights-close-button"
          onClick={() => {
            closeDialog();
            store.updateDocumentOrderStep(OrderStep.Confirm);
          }}
        >
          Close
        </Button>
      );
  }
};
